var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { styled, Typography } from '@mui/material';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { gray85 } from '../../colors/gray';
import { red03 } from '../../colors/red';
import SvgIcon from '../SvgIcon';
import { COMMON_STYLES, DEFAULT_ICON_COLOR, DEFAULT_ICON_SIZE, DEFAULT_SHAPE_COLOR, DEFAULT_SHAPE_HEIGHT, DEFAULT_SHAPE_WIDTH, HOVER_BRIGHTNESS_LEVEL, INACTIVE_BRIGHTNESS_LEVEL, INACTIVE_SATURATION_LEVEL, } from './_definitions/GisObjectDefinitions';
import { rem } from '../../utils/rem';
// Components.
var Root = styled('div', { name: 'YdsGisObjectIcon' })({
    alignContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    lineHeight: 1,
    maxWidth: 'max-content',
    pointerEvents: 'none',
});
export var IconShape = styled('div', { name: 'YdsGisIconShape' })({
    margin: '0 auto',
    pointerEvents: 'none',
    position: 'relative',
});
var GisObjectIcon = function (_a) {
    var _b = _a.disableBorder, disableBorder = _b === void 0 ? false : _b, _c = _a.hasError, hasError = _c === void 0 ? false : _c, _d = _a.hovered, hovered = _d === void 0 ? false : _d, icon = _a.icon, iconProps = _a.iconProps, _e = _a.inactive, inactive = _e === void 0 ? false : _e, _f = _a.interactive, interactive = _f === void 0 ? true : _f, label = _a.label, onClick = _a.onClick, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, shape = _a.shape, shapeProps = _a.shapeProps, sx = _a.sx, props = __rest(_a, ["disableBorder", "hasError", "hovered", "icon", "iconProps", "inactive", "interactive", "label", "onClick", "onMouseEnter", "onMouseLeave", "shape", "shapeProps", "sx"]);
    var _g = useState(hovered), isHovered = _g[0], setIsHovered = _g[1];
    var ref = createRef();
    /**
     * This `useEffect` is for adding some CSS styles and mouse enter&leave listeners on the topmost element of the SVG
     * (which represents the real shape, not the whole bounding box).
     * Dependencies: when the `interactive` and/or `disableBorder` flags change.
     */
    useEffect(function () {
        var _a, _b, _c;
        // @ts-ignore
        var el = (_b = (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.children) === null || _b === void 0 ? void 0 : _b.background;
        var handleMouseEnter = function (event) {
            setIsHovered(true);
            // Fire user-defined event.
            onMouseEnter === null || onMouseEnter === void 0 ? void 0 : onMouseEnter(event);
        };
        var handleMouseLeave = function (event) {
            setIsHovered(false);
            // Fire user-defined event.
            onMouseLeave === null || onMouseLeave === void 0 ? void 0 : onMouseLeave(event);
        };
        var handleOnClick = function (event) {
            // Fire user-defined event.
            onClick === null || onClick === void 0 ? void 0 : onClick(event);
        };
        var removeAllListeners = function () {
            if (el) {
                el.removeEventListener('mouseenter', handleMouseEnter);
                el.removeEventListener('mouseleave', handleMouseLeave);
                el.removeEventListener('click', handleOnClick);
            }
        };
        if (el) {
            // This is the most important line. Setting the `pointer-events` to `auto` ensures that only the shape element
            // will react to user's mouse actions.
            el.style.setProperty('pointer-events', 'auto');
            // Apply the stroke only in case the icon has actually any stroke already defined.
            if ((_c = el.attributes) === null || _c === void 0 ? void 0 : _c.stroke) {
                el.style.setProperty('stroke', disableBorder ? 'transparent' : hasError ? red03 : el.attributes.stroke.value);
            }
            if (interactive) {
                el.addEventListener('mouseenter', handleMouseEnter);
                el.addEventListener('mouseleave', handleMouseLeave);
                el.addEventListener('click', handleOnClick);
            }
            else {
                removeAllListeners();
            }
        }
        // Remove all listeners on component unmount.
        return function () {
            removeAllListeners();
        };
    }, [disableBorder, hasError, interactive]);
    var memoizedShapeStyles = useMemo(function () { return [
        {
            pointerEvents: 'none',
        },
        shapeProps.sx,
        inactive && {
            filter: "saturate(".concat(INACTIVE_SATURATION_LEVEL, ") brightness(").concat(INACTIVE_BRIGHTNESS_LEVEL, ")"),
        },
    ]; }, [inactive, shapeProps.sx]);
    var memoizedIconStyles = useMemo(function () {
        var _a, _b;
        return [
            {
                left: rem((_a = iconProps === null || iconProps === void 0 ? void 0 : iconProps.leftOffset) !== null && _a !== void 0 ? _a : (shapeProps.width - Number(iconProps === null || iconProps === void 0 ? void 0 : iconProps.width) || 0) / 2),
                opacity: inactive ? 0.5 : 1,
                pointerEvents: 'none',
                position: 'absolute',
                top: rem((_b = iconProps === null || iconProps === void 0 ? void 0 : iconProps.topOffset) !== null && _b !== void 0 ? _b : (shapeProps.height - Number(iconProps === null || iconProps === void 0 ? void 0 : iconProps.height) || 0) / 2),
            },
            iconProps === null || iconProps === void 0 ? void 0 : iconProps.sx,
        ];
    }, [iconProps]);
    return (React.createElement(Root, __assign({ sx: sx }, props),
        React.createElement(IconShape, { sx: {
                filter: "brightness(".concat(isHovered ? HOVER_BRIGHTNESS_LEVEL : 1, ")"),
            } },
            React.createElement(SvgIcon, { component: shape, height: rem((shapeProps === null || shapeProps === void 0 ? void 0 : shapeProps.height) || DEFAULT_SHAPE_HEIGHT), htmlColor: hasError ? 'white' : (shapeProps === null || shapeProps === void 0 ? void 0 : shapeProps.color) || DEFAULT_SHAPE_COLOR, ref: ref, 
                // @ts-ignore
                sx: memoizedShapeStyles, width: rem(shapeProps.width || DEFAULT_SHAPE_WIDTH) }),
            React.createElement(SvgIcon, { component: icon, height: rem((iconProps === null || iconProps === void 0 ? void 0 : iconProps.height) || DEFAULT_ICON_SIZE), htmlColor: hasError ? gray85 : (iconProps === null || iconProps === void 0 ? void 0 : iconProps.color) || DEFAULT_ICON_COLOR, 
                // @ts-ignore
                sx: memoizedIconStyles, width: rem((iconProps === null || iconProps === void 0 ? void 0 : iconProps.width) || DEFAULT_ICON_SIZE) })),
        label && (React.createElement(Typography, { variant: "caption", sx: COMMON_STYLES.label }, label))));
};
GisObjectIcon.displayName = 'GisObjectIcon';
export default GisObjectIcon;
